import { createContext, useContext, useReducer } from "react";
import { inflate, deflate } from "pako";
import { pack, unpack } from "msgpackr";

// Initial state
const getInitialState = (): DioramaState => {
	const defaultState: DioramaState = {
		currentStep: 1,
		background: null,
		characters: [],
		text: "Sample",
	};

	try {
		const urlParams = new URLSearchParams(window.location.search);
		const data = urlParams.get("data");

		if (data) {
			// Decode the URL parameter

			const compressedData = Uint8Array.from(
				atob(decodeURIComponent(data)),
				(c) => c.charCodeAt(0),
			);

			const serializedData = inflate(compressedData);

			const decodedData = unpack(serializedData);

			// Reconstruct full state from decoded data
			return {
				currentStep: 4, // Set to final preview step
				background: decodedData.bg,
				characters: decodedData.chars.map((c: any) => ({
					id: c.id,
					gender: c.g,
					image: c.imageSrc,
					slot: c.s,
					customization: c.cc,
				})),
				text: decodedData.txt || defaultState.text,
			};
		}
	} catch (error) {
		console.error("Error decoding diorama from URL:", error);
	}

	return defaultState;
};

// Action types
type Action =
	| { type: "SET_STEP"; step: number }
	| { type: "SET_BACKGROUND"; background: Background }
	| { type: "ADD_CHARACTER"; character: Character }
	| { type: "REMOVE_CHARACTER"; id: string }
	| { type: "UPDATE_TEXT"; text: string }
	| { type: "UPDATE_FONT_FAMILY"; fontFamily: string }
	| { type: "UPDATE_FONT_SIZE"; fontSize: number }
	| { type: "UPDATE_FONT_COLOR"; fontColor: string };

// Reducer
function dioramaReducer(state: DioramaState, action: Action): DioramaState {
	switch (action.type) {
		case "SET_STEP":
			return { ...state, currentStep: action.step };
		case "SET_BACKGROUND":
			return { ...state, background: action.background };
		case "ADD_CHARACTER":
			return { ...state, characters: [...state.characters, action.character] };
		case "REMOVE_CHARACTER":
			return {
				...state,
				characters: state.characters.filter((char) => char.id !== action.id),
			};
		case "UPDATE_TEXT":
			return { ...state, text: action.text };
		default:
			return state;
	}
}

// Context setup
type DioramaContextType = {
	state: DioramaState;
	dispatch: React.Dispatch<Action>;
	encodeToUrl: () => string;
};

const DioramaContext = createContext<DioramaContextType | undefined>(undefined);

export const DioramaProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [state, dispatch] = useReducer(dioramaReducer, getInitialState());

	// Function to encode state to URL
	const encodeToUrl = (): string => {
		const stateToEncode = {
			bg: state.background,
			chars: state.characters.map((c) => ({
				id: c.id,
				imageSrc: c.image,
				s: c.slot,
				cc: c.customization,
			})),
			txt: state.text,
		};

		const serializedState = pack(stateToEncode);
		const compressed = deflate(serializedState, { level: 9 });

		const base64Data = btoa(String.fromCharCode(...compressed));

		return `${window.location.origin}?data=${encodeURIComponent(base64Data)}`;
	};

	return (
		<DioramaContext.Provider value={{ state, dispatch, encodeToUrl }}>
			{children}
		</DioramaContext.Provider>
	);
};

// Custom hook for using the context
export const useDiorama = () => {
	const context = useContext(DioramaContext);
	if (context === undefined) {
		throw new Error("useDiorama must be used within a DioramaProvider");
	}
	return context;
};
